import { useRef } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import Close from '@icon/Close';
import { mediaQuery, Spacings } from '@styles/index';

const DrawerRoot = styled.div`
  position: fixed;
  z-index: 1300;
  &.open {
    top: 0;
    right: 0;
    height: 100vh;
    left: auto;
  }
`;

const DrawerContent = styled.div`
  background-color: var(--color-base-0);
  display: grid;
  grid-template-columns: repeat(10, ${Spacings.SPACING_8B});
  grid-column-gap: ${Spacings.SPACING_4B};
  padding: ${Spacings.SPACING_6B} 115px 0 ${Spacings.SPACING_12B};
  overflow-y: auto;
  transition: transform 225ms cubic-bezier(0, 0, 0.2, 1) 0ms;
  height: 100%;
  position: fixed;
  top: 0;
  right: 0;
  left: auto;
  z-index: 200;
  transform: translateX(100%);
  &.open {
    box-shadow: 0px 8px 10px -5px rgb(0 0 0 / 20%),
      0px 16px 24px 2px rgb(0 0 0 / 14%), 0px 6px 30px 5px rgb(0 0 0 / 12%);
    transform: translateX(0);
  }
  ${mediaQuery.uptoTablet} {
    padding: ${Spacings.SPACING_6B} 56px 0 ${Spacings.SPACING_12B};
  }
  ${mediaQuery.uptoMobile} {
    grid-template-columns: repeat(10, 1fr);
    grid-column-gap: ${Spacings.SPACING_2B};
    padding: ${Spacings.SPACING_4B};
  }
`;

const BackDrop = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  background-color: var(--color-overlay);
  z-index: 100;
  top: 0;
  right: 0;
  transition: transform 225ms cubic-bezier(0, 0, 0.2, 1) 0ms;
`;

const CloseWrapper = styled.div`
  cursor: pointer;
  background-color: var(--color-base-0);
  grid-column: span 10;
  padding-top: 32px;
  ${mediaQuery.uptoMobile} {
    padding: 0;
  }
`;

const ContentDrawer = ({ children, open, onClose, callback }) => {
  const ref = useRef(null);
  const className = open ? 'open' : '';
  useFirstRenderSkip(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      ref.current.addEventListener('transitionend', callback);
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      ref.current.removeEventListener('transitionend', callback);
    };
  }, [open]);

  return (
    <DrawerRoot role="presentation" className={className}>
      {open && <BackDrop onClick={onClose} aria-hidden="true" />}
      <DrawerContent className={className} ref={ref}>
        <CloseWrapper>
          <Close
            size="24"
            color="var(--color-text-primary)"
            onClick={onClose}
          />
        </CloseWrapper>
        {children}
      </DrawerContent>
    </DrawerRoot>
  );
};

ContentDrawer.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node)
  ]).isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  callback: PropTypes.func
};

ContentDrawer.defaultProps = {
  callback: () => {}
};

export default ContentDrawer;
